import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import type { ISupportNotification } from '../../../models/Support/ISupportNotification';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import {
	useMarkNotificationReadMutation,
	useMarkNotificationsReadMutation,
	useSupportNotificationsQuery,
	useSupportNotificationsUnreadCountQuery,
} from '../../../queries/Support/useSupportNotifications';
import { useSupportNotificationsRealtime } from '../../../queries/Support/useSupportNotificationsRealtime';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { SupportNotificationsBellView } from './SupportNotificationsBellView';

const FEED_PAGE_SIZE = 20;

function safeDecode(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

function getSupportPathFromClickUrl(clickUrl?: string) {
	if (!clickUrl) return null;

	try {
		const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
		const parsed = new URL(clickUrl, baseUrl);
		const hashPath = parsed.hash.replace(/^#/, '');
		const path = hashPath || `${parsed.pathname}${parsed.search}`;
		return path.startsWith('/support') ? path : null;
	} catch {
		return null;
	}
}

function normalizeSupportPath(path: string) {
	const [pathname, queryString = ''] = path.split('?');
	const queryParams = new URLSearchParams(queryString);
	const legacyTicketPathMatch = pathname.match(/^\/support\/ticket\/([^/?#]+)/);

	if (legacyTicketPathMatch) {
		queryParams.set('ticketId', safeDecode(legacyTicketPathMatch[1]));
		queryParams.set('view', 'desk');
		return `/support?${queryParams.toString()}`;
	}

	if (pathname === '/support' && queryParams.has('ticketId') && !queryParams.has('view')) {
		queryParams.set('view', 'desk');
		return `/support?${queryParams.toString()}`;
	}

	return path;
}

function getTicketSupportPath(ticketId: string, threadId?: string) {
	const queryParams = new URLSearchParams({ view: 'desk', ticketId });
	if (threadId) queryParams.set('threadId', threadId);
	return `/support?${queryParams.toString()}`;
}

function getNotificationSupportPath(notification: ISupportNotification) {
	if (notification.ticketId) {
		return getTicketSupportPath(notification.ticketId, notification.threadId);
	}

	const clickUrlSupportPath = getSupportPathFromClickUrl(notification.clickUrl);
	return clickUrlSupportPath ? normalizeSupportPath(clickUrlSupportPath) : null;
}

export const SupportNotificationsBell = observer(() => {
	const userSession = useAdminUserSession();
	const hasSupportAccess = userSession.canAccessSupport || userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const { data: mailboxes } = useSupportMailboxListQuery({ enabled: hasSupportAccess });

	if (!hasSupportAccess) return null;
	if (!mailboxes?.length) return null;
	return <SupportNotificationsBellContent />;
});

const SupportNotificationsBellContent: React.FC = () => {
	const history = useHistory();
	const [open, setOpen] = React.useState(false);

	const { data: unreadCount } = useSupportNotificationsUnreadCountQuery();
	const feedQuery = useSupportNotificationsQuery({ page: 1, pageSize: FEED_PAGE_SIZE }, { enabled: open });
	const markRead = useMarkNotificationReadMutation();
	const markBatchRead = useMarkNotificationsReadMutation();
	useSupportNotificationsRealtime();

	const notifications = feedQuery.data?.values ?? [];
	const totalCount = feedQuery.data?.totalCount ?? 0;

	const openNotification = (notification: ISupportNotification) => {
		// Marking read is best-effort; settled invalidation restores the true unread state.
		if (!notification.readDate) markRead.mutate(notification.id);
		setOpen(false);

		const supportPath = getNotificationSupportPath(notification);
		if (supportPath) {
			history.push(supportPath);
			return;
		}

		if (notification.clickUrl) {
			window.location.assign(notification.clickUrl);
		}
	};

	return (
		<SupportNotificationsBellView
			isOpen={open}
			onOpenChange={setOpen}
			notifications={notifications}
			unreadCount={unreadCount ?? 0}
			totalCount={totalCount}
			isLoading={feedQuery.isLoading}
			isError={feedQuery.isError}
			isMarkAllReadPending={markBatchRead.isPending}
			onNotificationClick={openNotification}
			onMarkAllRead={() => markBatchRead.mutate(undefined)}
		/>
	);
};
